import { useCallback } from 'react';
import printJS from 'print-js';
import { InvoiceModel, getInvoiceReceipt, getRefundReceipt } from '@frontend/api-invoices';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';

type PrintReceiptOptions = {
  receiptType: 'payment' | 'refund';
  refundId?: string;
};

const PAYMENT_RECEIPT_PRINT_CSS = `
  table tbody tr:first-of-type td {
    background-color: #fff;
    border-bottom: 1px solid #aaa;
    color: #000;
  }
`;

const REFUND_RECEIPT_PRINT_CSS = `
  html { transform: scale(0.7) translateY(-120px); }
  .mj-column-per-50 { width:50% !important; max-width: 50%; }
  .overview-header { border: solid 1px #E2E7F1; }
`;

const formatRefundReceipt = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const companyHeader = doc.getElementsByClassName('company-header');
  if (companyHeader.length) companyHeader?.[0]?.remove();

  const weaveFooter = doc.getElementsByClassName('weave');
  if (weaveFooter.length) weaveFooter?.[0]?.remove();

  const serializer = new XMLSerializer();
  return serializer.serializeToString(doc);
};

export const usePrintReceipt = () => {
  const alerts = useAlert();
  const { t } = useTranslation('payments');

  return useCallback(
    async (invoice: InvoiceModel, options?: PrintReceiptOptions) => {
      const receiptType = options?.receiptType ?? 'payment';

      if (receiptType === 'refund' && !invoice.payment?.hasRefund) {
        alerts.error(t("Payment doesn't have a refund."));
        console.error("Payment doesn't have a refund.");
        return;
      }

      const paymentId = invoice.payment?.paymentId;
      const refundId = options?.refundId;

      if (receiptType === 'refund' && (!refundId || !paymentId)) {
        alerts.error(t('Print failed. Receipt not found.'));
        console.error('Refund id required to print a refund receipt');
        return;
      }

      try {
        alerts.info(t('Printing receipt...'));

        const response =
          receiptType === 'refund' && paymentId && refundId
            ? await getRefundReceipt(paymentId, refundId)
            : await getInvoiceReceipt(invoice);

        if (!response) {
          alerts.error(t('Print failed. Receipt not found.'));
          console.error('Print failed. Receipt not found.');
          return;
        }

        const receipt = receiptType === 'refund' ? formatRefundReceipt(response) : response;

        printJS({
          documentTitle: invoice.person
            ? t('Receipt for {{name}} {{date}}', {
                name: invoice.person.name,
                date: formatDate(invoice.billedAt, 'MMM D, YYYY'),
              })
            : t('Receipt for invoice {{invoiceId}}', { invoiceId: invoice.id }),
          printable: receipt,
          style: receiptType === 'refund' ? REFUND_RECEIPT_PRINT_CSS : PAYMENT_RECEIPT_PRINT_CSS,
          type: 'raw-html',
        });
      } catch (error) {
        alerts.error(t('An error occurred while printing receipt.'));
        console.error(error);
      }
    },
    [alerts, t]
  );
};
